import React, { useLayoutEffect, useRef, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  Gamepad2, 
  Users, 
  Code, 
  BookOpen, 
  Building2,
  UserPlus,
  LogIn,
  LogOut,
  User,
  Settings,
  ArrowUpRight,
  ShoppingCart
} from 'lucide-react';
import './CardNav.css';

const CardNav: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement[]>([]);
  const location = useLocation();
  const { currentUser, userData, logout, isAdmin } = useAuth();

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    if (navRef.current) {
      navRef.current.style.height = '60px';
      
      // Reverse animation for cards
      cardsRef.current.forEach((card, index) => {
        if (card) {
          setTimeout(() => {
            card.style.transform = 'translateY(50px)';
            card.style.opacity = '0';
          }, index * 20);
        }
      });
    }
  }, []);

  // Sayfa değişiminde menüyü kapat
  useEffect(() => {
    // Hemen menüyü kapat
    closeMenu();
    
    // Ek güvenlik için timeout ile de kapat
    const timeoutId = setTimeout(() => {
      closeMenu();
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, closeMenu]);

  // Menü durumuna göre tabIndex kontrolü
  useEffect(() => {
    const contentElement = document.querySelector('.card-nav-content');
    if (contentElement) {
      const links = contentElement.querySelectorAll('a');
      links.forEach(link => {
        if (isMenuOpen) {
          link.removeAttribute('tabIndex');
        } else {
          link.setAttribute('tabIndex', '-1');
        }
      });
    }
  }, [isMenuOpen]);

  const allNavItems = [
    { path: '/', label: 'Ana Sayfa', icon: Gamepad2 },
    { path: '/servers', label: 'Sunucular', icon: Users },
    { path: '/market', label: 'Market', icon: ShoppingCart },
    { path: '/sponsors', label: 'Sponsorlar', icon: Building2 },  
    { path: '/scripts', label: 'Scriptler', icon: Code },
    { path: '/guide', label: 'Rehber', icon: BookOpen }
  ];

  const calculateHeight = () => {
    const navEl = navRef.current;
    if (!navEl) return 700; // Even more increased default height

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      // Mobilde sabit yükseklik kullan (scroll için)
      return 80; // 60px top bar + 20px padding
    }
    return 700; // Even more increased default height
  };

  useLayoutEffect(() => {
    const navEl = navRef.current;
    if (!navEl) return;

    // Initial state
    navEl.style.height = '60px';
    navEl.style.overflow = 'hidden';
    
    cardsRef.current.forEach(card => {
      if (card) {
        card.style.transform = 'translateY(50px)';
        card.style.opacity = '0';
        card.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
      }
    });
  }, []);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (isMenuOpen) {
        const newHeight = calculateHeight();
        if (navRef.current) {
          navRef.current.style.height = `${newHeight}px`;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  // Dışarı tıklandığında kapatma
  useLayoutEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        if (isMenuOpen) {
          closeMenu();
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, closeMenu]);

  const openMenu = () => {
    setIsMenuOpen(true);
    if (navRef.current) {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        navRef.current.style.height = '90vh';
      } else {
        const height = calculateHeight();
        navRef.current.style.height = `${height}px`;
      }
      
      // Staggered animation for cards
      cardsRef.current.forEach((card, index) => {
        if (card) {
          setTimeout(() => {
            card.style.transform = 'translateY(0)';
            card.style.opacity = '1';
          }, index * 80);
        }
      });
    }
  };

  const toggleMenu = () => {
    if (!isMenuOpen) {
      openMenu();
    } else {
      closeMenu();
    }
  };

  const setCardRef = (i: number) => (el: HTMLDivElement | null) => {
    if (el) cardsRef.current[i] = el;
  };

  const getCardLinks = (category: string) => {
    switch (category) {
      case 'Ana Sayfa':
        return [
          { label: 'Hakkımızda', href: '/', icon: ArrowUpRight },
          { label: 'İletişim', href: '/contact', icon: ArrowUpRight }
        ];
      case 'Sunucular':
        return [
          { label: 'Sunucu Listesi', href: '/servers', icon: ArrowUpRight },
          { label: 'Sunucu Durumu', href: '/servers/status', icon: ArrowUpRight }
        ];
      case 'Market':
        return [
          { label: 'Oyun Ici Market', href: '/market', icon: ArrowUpRight },

        ];
      case 'Sponsorlar':
        return [
          { label: 'Sponsorlar', href: '/sponsors', icon: ArrowUpRight },
          { label: 'Sponsor Ol', href: '/sponsors/apply', icon: ArrowUpRight }
        ];
      case 'Scriptler':
        return [
          { label: 'Script Koleksiyonu', href: '/scripts', icon: ArrowUpRight },
          { label: 'Script Geliştirme', href: '/scripts/dev', icon: ArrowUpRight }
        ];
    case 'Rehber':
      return [
        { label: 'Başlangıç Rehberi', href: '/guide', icon: ArrowUpRight },
        { label: 'Sık Sorulan Sorular', href: '/guide/faq', icon: ArrowUpRight },
        { label: 'Sunucu Kuralları', href: '/rules', icon: ArrowUpRight },
        { label: 'Topluluk Kuralları', href: '/rules/community', icon: ArrowUpRight }
      ];
      default:
        return [];
    }
  };

  return (
    <>
      <div className={`card-nav-overlay ${isMenuOpen ? 'open' : ''}`} onClick={() => {
        if (isMenuOpen) {
          closeMenu();
          if (navRef.current) {
            navRef.current.style.height = '60px';
            
            // Reverse animation for cards
            cardsRef.current.forEach((card, index) => {
              if (card) {
                setTimeout(() => {
                  card.style.transform = 'translateY(50px)';
                  card.style.opacity = '0';
                }, index * 40);
              }
            });
          }
        }
      }} />
      <div className="card-nav-container">
        <nav ref={navRef} className={`card-nav ${isMenuOpen ? 'open' : ''}`}>
        <div className="card-nav-top">
        <div
          className={`hamburger-menu ${isMenuOpen ? 'open' : ''}`}
          onClick={toggleMenu}
          role="button"
          aria-label={isMenuOpen ? 'Menüyü Kapat' : 'Menüyü Aç'}
          tabIndex={0}
        >
            <div className="hamburger-line" />
            <div className="hamburger-line" />
          </div>

                 <Link to="/" className="logo-container" onClick={() => closeMenu()}>
                   <img src="/aedon.png" alt="Aedon Logo" className="logo" />
                   <span className="logo-text">Aedon Community</span>
                 </Link>

          {currentUser ? (
            <Link
              to="/profile"
              className="card-nav-cta-button"
              onClick={() => {
                closeMenu();
              }}
            >
              <User className="w-4 h-4 mr-2" />
              {userData?.username || 'Kullanıcı'}
            </Link>
          ) : (
            <Link
              to="/login"
              className="card-nav-cta-button"
              onClick={() => {
                closeMenu();
              }}
            >
              <LogIn className="w-4 h-4 mr-2" />
              Giriş Yap
            </Link>
          )}
        </div>

        <div 
          className={`card-nav-content ${!isMenuOpen ? 'inert' : ''}`}
          aria-hidden={!isMenuOpen}
        >
          {allNavItems.map((item, idx) => (
            <div
              key={`${item.label}-${idx}`}
              className="nav-card"
              ref={setCardRef(idx)}
            >
              <div className="nav-card-label">{item.label}</div>
              <div className="nav-card-links">
                {getCardLinks(item.label).map((link, i) => (
                  <Link
                    key={`${link.label}-${i}`}
                    className="nav-card-link"
                    to={link.href}
                    onClick={() => {
                      // Hemen menüyü kapat
                      closeMenu();
                      
                      // Ek güvenlik için timeout ile de kapat
                      setTimeout(() => {
                        closeMenu();
                      }, 100);
                    }}
                  >
                    <link.icon className="nav-card-link-icon" aria-hidden="true" />
                    {link.label}
                  </Link>
                ))}
              </div>
        </div>
      ))}
      
                 {/* Alt çizgi ve Kimlik doğrulama butonları */}
                 <div className="auth-section-mobile">
                   <div className="auth-divider"></div>
                   {currentUser ? (
                     // Kullanıcı giriş yapmış
                     <div className="auth-buttons-container">
                       {isAdmin && (
                         <>
                           <Link
                             to="/admin"
                             className="auth-button-mobile admin-button"
                             onClick={() => {
                               closeMenu();
                             }}
                           >
                             <Settings className="w-5 h-5" />
                             Admin
                           </Link>
                           <div className="auth-button-divider"></div>
                         </>
                       )}
                       <button
                         className="auth-button-mobile logout-button"
                         onClick={() => {
                           logout();
                           closeMenu();
                         }}
                       >
                         <LogOut className="w-5 h-5" />
                         Çıkış Yap
                       </button>
                     </div>
                   ) : (
                     // Kullanıcı giriş yapmamış
                     <div className="auth-buttons-container">
                       <Link
                         to="/login"
                         className="auth-button-mobile login-button"
                         onClick={() => {
                           closeMenu();
                         }}
                       >
                         <LogIn className="w-5 h-5" />
                         Giriş Yap
                       </Link>
                       <div className="auth-button-divider"></div>
                       <Link
                         to="/register"
                         className="auth-button-mobile register-button"
                         onClick={() => {
                           closeMenu();
                         }}
                       >
                         <UserPlus className="w-5 h-5" />
                         Kayıt Ol
                       </Link>
                     </div>
                   )}
                 </div>
    </div>
        </nav>
      </div>
    </>
  );
};

export default CardNav;
